import {
  ActionNames,
  HostEditActionsDictionary,
  NativeActionNames,
  NativeActionsList,
} from '@wix/editor-platform-host-integration-apis';
import { EditAction, ContributedEditActionsDict, ActionUI } from './types';
import { platformSystemActions } from './systemActions';
import {
  createEditorPlatformEditActionsError,
  EditorPlatformEditActionsErrorCode,
} from './errors';

const nativeActionNamesSet = new Set<string>(Object.values(NativeActionsList));

const nativeActionsStore = new Map<NativeActionNames, EditAction>();

const systemActionsStore = new Map<string, EditAction>(
  Object.entries(platformSystemActions) as [string, EditAction][],
);

const UIOverridesStore = new Map<string, ActionUI>();

const isNativeAction = (name: string): name is NativeActionNames =>
  nativeActionNamesSet.has(name);

const setAction = (actionName: ActionNames, action: EditAction) => {
  if (isNativeAction(actionName)) {
    nativeActionsStore.set(actionName, action);
  } else {
    systemActionsStore.set(actionName, action);
  }
};

const setActions = (actions: ContributedEditActionsDict) => {
  const actionNames = Object.keys(actions) as ActionNames[];
  actionNames.forEach((actionName) => {
    setAction(actionName, actions[actionName]!);
  });
};

const getActionByActionName = (actionName: ActionNames) => {
  if (isNativeAction(actionName)) {
    return nativeActionsStore.get(actionName) ?? null;
  }
  return systemActionsStore.get(actionName) ?? null;
};

const contributeActions = (actions: Partial<HostEditActionsDictionary>) => {
  setActions(actions);
};

const getActions = () => {
  return [
    ...Array.from(nativeActionsStore.values()),
    ...Array.from(systemActionsStore.values()),
  ];
};

const getActionsByActionNames = (actionNames: ActionNames[]) => {
  return actionNames
    .map((actionName) => getActionByActionName(actionName)!)
    .filter((action) => !!action);
};

const getSystemAction = (actionName: string) => {
  return systemActionsStore.get(actionName) ?? null;
};

const getSystemActionNames = () => {
  return Array.from(systemActionsStore.keys());
};

const overrideActionUI = (actionName: string, actionUI: ActionUI) => {
  if (
    !nativeActionsStore.has(actionName as NativeActionNames) &&
    !systemActionsStore.has(actionName)
  ) {
    throw createEditorPlatformEditActionsError(
      EditorPlatformEditActionsErrorCode.EditActionToOverrideNotFound,
      `Cannot set UI override for action "${actionName}" that does not exist in the store.
      Please make sure that "${actionName}" action was contributed.`,
    );
  }
  UIOverridesStore.set(actionName, actionUI);
};

const getActionUIOverride = (actionName: string) => {
  return UIOverridesStore.get(actionName) ?? undefined;
};

const getActionUIOverrides = (actionNames: string[]) => {
  return actionNames
    .map((actionName) => getActionUIOverride(actionName))
    .filter((actionUI) => !!actionUI) as ActionUI[];
};

export const EditActionsStore = {
  setAction,
  setActions,
  getActions,
  getActionByActionName,
  getActionsByActionNames,
  contributeActions,
  overrideActionUI,
  getActionUIOverride,
  getActionUIOverrides,
  getSystemAction,
  getSystemActionNames,
};
